import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  CaptionedLoader,
  IStationTimingPreviewSelection,
  IStationTimingPreviewStation,
  StationTimingPreview,
} from "@ryde/controls";
import { IStationTimingPairStatsDto, SimpleResult } from "@ryde/shared";

import { RideMeasuredTimesStatusStyled } from "./ride-measured-times-content.styled";
import { useRideMeasuredTimesStats } from "./use-ride-measured-times-stats";

interface IRideMeasuredTimesContentProps {
  /** Already in flight when the dialog opens; the dialog shows the loader until it settles. */
  statsPromise: Promise<SimpleResult<IStationTimingPairStatsDto>>;
  stations: IStationTimingPreviewStation[];
  plannedLegSeconds: (number | undefined)[];
  basisNote: string;
  onChange: (selection: IStationTimingPreviewSelection) => void;
}

export const RideMeasuredTimesContent: FC<IRideMeasuredTimesContentProps> = ({
  statsPromise,
  stations,
  plannedLegSeconds,
  basisNote,
  onChange,
}) => {
  const { t } = useTranslation();
  const { errorMessage, stats } = useRideMeasuredTimesStats(statsPromise);

  // The dialog stays open and says what happened rather than closing itself out from under the user.
  if (errorMessage) {
    return (
      <RideMeasuredTimesStatusStyled>
        <p className="ride-measured-times__message" role="status">
          {errorMessage}
        </p>
      </RideMeasuredTimesStatusStyled>
    );
  }

  if (!stats) {
    return (
      <RideMeasuredTimesStatusStyled>
        <CaptionedLoader
          caption={t("stationTiming.MEASURED_TIMES_CALCULATING")}
        />
      </RideMeasuredTimesStatusStyled>
    );
  }

  return (
    <StationTimingPreview
      stations={stations}
      plannedLegSeconds={plannedLegSeconds}
      stats={stats}
      onChange={onChange}
      basisNote={basisNote}
    />
  );
};
